import { useState, useEffect, useMemo } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { Button } from '../../../shared/components/ui/button';
import { Input } from '../../../shared/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../../../shared/components/ui/dropdown-menu';

type Category = {
  id: string;
  name: string;
};

type Specialty = {
  id: string;
  name: string;
  categoryId: string | null;
};

type SpecialtyFilterDropdownProps = {
  specialties: Specialty[];
  categories: Category[];
  selectedSpecialtyIds: string[];
  onChange: (specialtyIds: string[]) => void;
  isLoading?: boolean;
};

export const SpecialtyFilterDropdown = ({
  specialties,
  categories,
  selectedSpecialtyIds,
  onChange,
  isLoading = false,
}: SpecialtyFilterDropdownProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Clear search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  // Filter specialties based on search term
  const filteredSpecialties = useMemo(() => {
    return specialties.filter((specialty) =>
      specialty.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [specialties, searchTerm]);

  // Group filtered specialties by category
  const groupedSpecialties = useMemo(() => {
    const groups: Record<string, Specialty[]> = {};

    filteredSpecialties.forEach((specialty) => {
      if (!specialty.categoryId) {
        return;
      }

      if (!groups[specialty.categoryId]) {
        groups[specialty.categoryId] = [];
      }
      groups[specialty.categoryId].push(specialty);
    });

    return groups;
  }, [filteredSpecialties]);

  // Get categories that have specialties (after filtering)
  const categoriesWithSpecialties = useMemo(() => {
    return categories.filter((category) => groupedSpecialties[category.id]?.length > 0);
  }, [categories, groupedSpecialties]);

  const handleSpecialtyToggle = (specialtyId: string) => {
    const isSelected = selectedSpecialtyIds.includes(specialtyId);

    if (isSelected) {
      onChange(selectedSpecialtyIds.filter((id) => id !== specialtyId));
    } else {
      onChange([...selectedSpecialtyIds, specialtyId]);
    }
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const getButtonLabel = () => {
    if (isLoading) {
      return 'Loading...';
    }

    if (selectedSpecialtyIds.length === 0) {
      return 'Select specialties';
    }

    return `${selectedSpecialtyIds.length} selected`;
  };

  const hasSelections = selectedSpecialtyIds.length > 0;
  const hasSpecialties = filteredSpecialties.length > 0;

  return (
    <div>
      <label className="text-sm text-gray-700 mb-3 block">Specialty</label>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between text-sm text-gray-700"
            disabled={isLoading}
            aria-label="Specialty filter"
            aria-haspopup="menu"
          >
            <span className={selectedSpecialtyIds.length === 0 ? 'text-gray-500' : 'text-gray-700'}>
              {getButtonLabel()}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[320px] p-0"
          align="start"
          role="menu"
          onCloseAutoFocus={(e) => {
            // Prevent auto-focus on trigger when closing
            e.preventDefault();
          }}
        >
          {/* Search Input */}
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search specialties..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 h-9 text-sm"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                autoFocus={false}
              />
            </div>
          </div>

          {/* Clear All Button */}
          {hasSelections && (
            <>
              <div className="px-3 py-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearAll}
                  className="w-full justify-start text-sm text-gray-600 hover:text-gray-900"
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear All
                </Button>
              </div>
              <DropdownMenuSeparator />
            </>
          )}

          {/* Specialty List (Grouped by Category) */}
          <div className="max-h-[400px] overflow-y-auto">
            {specialties.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                No specialties available
              </div>
            ) : !hasSpecialties ? (
              <div className="p-4 text-center text-sm text-gray-500">
                No specialties found
              </div>
            ) : (
              <div className="py-2">
                {categoriesWithSpecialties.map((category) => (
                  <div key={category.id} className="mb-2 last:mb-0">
                    {/* Category Header */}
                    <DropdownMenuLabel className="px-3 py-1.5 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      {category.name}
                    </DropdownMenuLabel>

                    {/* Specialties in this category */}
                    <div className="px-3 space-y-3">
                      {groupedSpecialties[category.id].map((specialty) => (
                        <DropdownMenuCheckboxItem
                          key={specialty.id}
                          checked={selectedSpecialtyIds.includes(specialty.id)}
                          onCheckedChange={() => handleSpecialtyToggle(specialty.id)}
                          onSelect={(e) => {
                            // Prevent dropdown from closing on selection
                            e.preventDefault();
                          }}
                          className="cursor-pointer text-sm text-gray-700 flex items-center gap-2"
                        >
                          {specialty.name}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

